<template>
  <div class="vacancy-test">
    <vtitle />

    <vacancy-stage-title
      :name="name"
      :description="description"
      :is-loading="isLoading"
      @set-name="name = $event"
      @set-description="description = $event"
    />

    <vpreloader v-if="isLoading" />

    <template v-else>
      <div class="vacancy-test__questions-views">
        <div
          class="vacancy-test__questions-view"
          :class="{ active: vacancyTest.question_page_limit === 0 }"
          @click="vacancyTest.question_page_limit = 0"
        >
          <vfocus-field class="pointer">
            <vcircle :value="vacancyTest.question_page_limit === 0" />
          </vfocus-field>

          <img class="vacancy-test__questions-view-icon" src="/static/images/vacancy/page_view_sing_w.svg" alt="" />

          <label>{{ $tr('vacancy.tests.form.pageview.single') }}</label>
        </div>

        <div
          class="vacancy-test__questions-view"
          :class="{ active: vacancyTest.question_page_limit === 1 }"
          @click="vacancyTest.question_page_limit = 1"
        >
          <vfocus-field class="pointer">
            <vcircle :value="vacancyTest.question_page_limit === 1" />
          </vfocus-field>

          <img class="vacancy-test__questions-view-icon" src="/static/images/vacancy/page_view_one_w.svg" alt="" />

          <label>{{ $tr('vacancy.tests.form.pageview.separate_per_question') }}</label>
        </div>
      </div>

      <div class="vacancy-test__options">
        <div class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.is_answer_paste_disabled"
            :label="$tr('vacancy.tests.form.is_answer_paste_disabled.checkbox')"
          />
        </div>

        <div class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.question_shuffle"
            :label="$tr('vacancy.tests.form.is_question_shuffle.checkbox')"
            :tooltip="{
              content: $tr('vacancy.tests.form.randomiztion.tooltip'),
              placement: 'auto',
              classes: 'tight'
            }"
          />

          <div class="vacancy-test__time-wrapper">
            <vinput
              v-model="vacancyTest.question_limit"
              valuetype="number"
              class="input_block"
              :disabled="!vacancyTest.question_shuffle"
              center
              :min="1"
              :max="vacancyTestQuestions.length"
              :placeholder="$tr('vacancy.tests.form.question_limit.input')"
            />
          </div>
        </div>

        <div class="vacancy-test__option">
          <vchecklabel v-model="vacancyTest.answer_shuffle" :label="$tr('vacancy.settings.answers-randomization')" />
        </div>

        <div class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.calculate_test_status"
            :label="$tr('vacancy.tests.form.required_score.checkbox')"
            :tooltip="{
              content: $tr('vacancy.tests.form.required_score.tooltip'),
              placement: 'auto',
              classes: 'tight'
            }"
          />

          <div class="vacancy-test__time-wrapper">
            <vinput
              v-model="vacancyTest.required_score"
              class="input_block"
              valuetype="number"
              :disabled="!vacancyTest.calculate_test_status"
              :min="1"
              :max="vacancyTestQuestions.length"
              center
            />
          </div>
        </div>

        <div v-if="isTestDropoutActive" class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.is_dropout_enabled"
            :label="`${$tr('vacancy.tests.form.test_dropdout.checkbox')} ${vacancyTest.required_score}`"
            :tooltip="{
              content: $tr('vacancy.tests.form.test_dropdout.tooltip'),
              placement: 'right',
              classes: 'tight'
            }"
          />
        </div>

        <div v-if="vacancyTest.question_page_limit === 0" class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.time_limit_enabled"
            :label="$tr('vacancy.tests.form.time_limit_enabled.checkbox')"
            :tooltip="{
              content: $tr('vacancy.tests.form.timelimit.tooltip'),
              placement: 'auto',
              classes: 'tight'
            }"
          />

          <div class="vacancy-test__time-wrapper">
            <masked-input
              v-model="fullTestLimit.h"
              :class="{
                error:
                  (isTimerLessDuration && vacancyTest.time_limit_enabled) ||
                  (!isTimeValid && vacancyTest.time_limit_enabled)
              }"
              :mask="[/[0-9]/, /[0-9]/]"
              keep-char-positions
              :disabled="!vacancyTest.time_limit_enabled"
              class="input_block"
              type="text"
              placeholder="00"
              @input="setTimeLimit"
            />

            <span>:</span>

            <masked-input
              v-model="fullTestLimit.m"
              class="input_block"
              :class="{
                error:
                  (isTimerLessDuration && vacancyTest.time_limit_enabled) ||
                  (!isTimeValid && vacancyTest.time_limit_enabled)
              }"
              type="text"
              placeholder="00"
              :mask="[/[0-5]/, /[0-9]/]"
              keep-char-positions
              :disabled="!vacancyTest.time_limit_enabled"
              @input="setTimeLimit"
            />

            <span>:</span>

            <masked-input
              v-model="fullTestLimit.s"
              class="input_block"
              :class="{
                error:
                  (isTimerLessDuration && vacancyTest.time_limit_enabled) ||
                  (!isTimeValid && vacancyTest.time_limit_enabled)
              }"
              type="text"
              placeholder="00"
              :mask="[/[0-5]/, /[0-9]/]"
              keep-char-positions
              :disabled="!vacancyTest.time_limit_enabled"
              @input="setTimeLimit"
            />
          </div>
        </div>

        <div v-if="vacancyTest.question_page_limit === 0 && hasAnticheating" class="vacancy-test__option">
          <vchecklabel
            v-model="vacancyTest.is_camera_required"
            :label="$tr('vacancy.settings.modules.anticheating.checkbox')"
            :tooltip="{
              content: $tr('vacancy.settings.modules.anticheating.tooltip'),
              placement: 'auto',
              classes: 'tight'
            }"
          />
        </div>

        <div
          v-if="vacancyTest.time_limit_enabled && isTimerLessDuration && vacancyTest.question_page_limit === 0"
          class="vacancy-test__option"
        >
          <div class="vacancy-test__time-error">
            {{ $tr('vacancy.tests.media.timeLess', { minutes: Math.ceil(allQuestionsDuration / 60) }) }}
          </div>
        </div>
      </div>

      <div v-if="vacancyTestQuestions.length > 0" ref="vacancyTestQuestions" class="vacancy-test__questions">
        <container
          group-name="questions"
          lock-axis="y"
          drag-handle-selector=".question-block__drag"
          @drop="questionOnDrop"
        >
          <draggable v-for="(item, index) in vacancyTestQuestions" :key="item._id">
            <vacancy-test-question
              :item="item"
              :is-answers-sort-enabled="!vacancyTest.answer_shuffle"
              :is-saving="isSaving"
              :index="index"
              :is-page-single="vacancyTest.question_page_limit === 0"
              :is-delete-disabled="vacancyTestQuestions.length <= 1"
              :is-show-timer="vacancyTest.question_page_limit === 1"
            />
          </draggable>
        </container>
        <!-- <sortable-list v-model="questionsComputed" @start="drag = true" @stop="drag = false">
          <transition-group type="transition">
            <sortable-item v-for="(item, index) in questionsComputed" :key="item._id">
              <vacancy-test-question
                :item="item"
                :is-saving="isSaving"
                :index="index"
                :is-page-single="vacancyTest.question_page_limit === 0"
                :is-delete-disabled="vacancyTestQuestions.length <= 1"
                :is-show-timer="vacancyTest.question_page_limit === 1"
              />
            </sortable-item>
          </transition-group>
        </sortable-list> -->
      </div>

      <div ref="buttons" class="vacancy-test__buttons" :class="{ sticky: isStickySave }">
        <vbutton label="vacancy.tests.form.button.add_question" :disabled="isSaving" type="add" @click="addQuestion" />

        <div>
          <vbutton
            label="common.save"
            :disabled="(vacancyTest.time_limit_enabled && vacancyTest.time_limit === 0) || isSomeErrors"
            :loading="isSaving"
            @click="saveTest()"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { cloneDeep, debounce } from 'lodash';
import { cleanTimeValue } from '@/_helpers/utils.js';
import { Container, Draggable } from 'vue-smooth-dnd';
import PROCESS_NAMES from '@/process/names.js';
import MaskedInput from 'vue-text-mask';
import Vtitle from '@/routes_new/inner/vacancy/components/vtitle.vue';
import VacancyStageTitle from '@/routes_new/inner/vacancy/components/VacancyStageTitle.vue';
import VacancyTestQuestion from '@/routes_new/inner/vacancy/test/VacancyTestQuestion.vue';
import Axios from 'axios';

export default {
  components: {
    Vtitle,
    VacancyStageTitle,
    MaskedInput,
    VacancyTestQuestion,
    Container,
    Draggable
  },

  data() {
    return {
      PROCESS_NAMES,
      isLoading: false,
      isSaving: false,
      name: '',
      description: '',
      vacancyTest: {
        is_answer_paste_disabled: false,
        question_page_limit: 0,
        question_shuffle: false,
        answer_shuffle: false,
        question_limit: 0,
        calculate_test_status: false,
        required_score: 0,
        time_limit: 0,
        is_dropout_enabled: false,
        time_limit_enabled: false
      },

      fullTestLimit: { h: '', m: '', s: '' },
      timeRegex: /[0-9][0-9]:[0-5][0-9]:[0-5][0-9]/g,
      isStickySave: false
    };
  },

  computed: {
    ...mapState('vacancy_new', { vacancy: 'item', modules: state => state.modules.list }),

    ...mapState('vacancyTests', ['vacancyTestQuestions', 'vacancyTestCharts']),

    ...mapGetters('vacancyTests', ['isSomeErrors', 'getChartConfigByQuestionId']),

    ...mapGetters('vacancy_new/modules', ['hasAnticheating']),

    ...mapGetters('process', ['getProcessName']),

    isTestDropoutActive() {
      const testDropout = this.modules.find(item => item.id == 'test-dropout');

      return testDropout.is_active;
    },

    allQuestionsDuration() {
      let allQuestionsDuration = 0; // in seconds

      this.vacancyTestQuestions.forEach(x => {
        allQuestionsDuration += x.duration;
      });

      return allQuestionsDuration;
    },

    isTimerLessDuration() {
      if (!this.vacancyTest.time_limit || !this.allQuestionsDuration) return false;

      return this.vacancyTest.time_limit <= this.allQuestionsDuration;
    },

    isTimeValid() {
      if (!this.vacancyTest.time_limit_enabled) return true;

      const timeLimitConcat = `${this.fullTestLimit.h}:${this.fullTestLimit.m}:${this.fullTestLimit.s}`;

      if (!timeLimitConcat) return false;

      return !!timeLimitConcat.match(this.timeRegex) && !!timeLimitConcat.match(this.timeRegex).length;
    }
  },

  async created() {
    this.isLoading = true;

    await this.fetchVacancyStages();

    await this.fetchTests(this.vacancy.id)
      .catch(err => {
        console.log(err);
      })
      .then(response => {
        this.vacancyTest = response;

        // set fullTimeLimit
        if (this.vacancyTest.time_limit === 0) {
          this.fullTestLimit.m = '02';
        }

        if (this.vacancyTest.time_limit > 86400) {
          const duration = this.$moment.duration(this.vacancyTest.time_limit, 'seconds');
          const time = duration.format('HH:mm:ss').split(':');

          this.fullTestLimit.h = time[0];
          this.fullTestLimit.m = time[1];
          this.fullTestLimit.s = time[2];
        } else {
          const time = this.$moment
            .utc(this.vacancyTest.time_limit * 1000)
            .format('HH:mm:ss')
            .split(':');

          this.fullTestLimit.h = time[0];
          this.fullTestLimit.m = time[1];
          this.fullTestLimit.s = time[2];
        }
      })
      .finally(async () => {
        this.isLoading = false;
      });
  },

  mounted() {
    window.addEventListener('scroll', this.checkIsStickySave);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.checkIsStickySave);
  },

  methods: {
    ...mapMutations('vacancyTests', ['ADD_QUESTION', 'QUESTIONS_SORT', 'ADD_EMPTY_CHARTCONFIG']),

    ...mapActions('vacancyTests', ['fetchTests']),

    async fetchVacancyStages() {
      this.isLoading = true;

      await Axios.get(`/v3/vacancy-stages/test/${this.vacancy.id}`)
        .then(response => {
          this.name = response.data.name;
          this.description = response.data.description;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async saveVacancyStages() {
      this.isSaving = true;

      await Axios.patch(`/v3/vacancy-stages/test/${this.vacancy.id}`, {
        name: this.name,
        description: this.description
      })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    addQuestion() {
      const newQuestionId = Date.now();

      this.ADD_EMPTY_CHARTCONFIG({ questionId: newQuestionId });
      this.ADD_QUESTION(newQuestionId);
    },

    questionOnDrop(dragResult) {
      const { removedIndex, addedIndex, payload } = dragResult;
      if (removedIndex === null && addedIndex === null) return this.vacancyTestQuestions;

      const result = [...this.vacancyTestQuestions];
      let itemToAdd = payload;

      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      this.QUESTIONS_SORT(result);
    },

    setTimeLimit() {
      let time = `${this.fullTestLimit.h ? this.fullTestLimit.h : '00'}:`;

      time += `${this.fullTestLimit.m ? this.fullTestLimit.m : '00'}:`;
      time += `${this.fullTestLimit.s ? this.fullTestLimit.s : '00'}`;

      this.vacancyTest.time_limit = !this.vacancyTest.time_limit_enabled ? 0 : cleanTimeValue(time);
    },

    // debounce is there for optimization of scroll listener
    checkIsStickySave: debounce(function () {
      const questionsDiv = this.$refs.vacancyTestQuestions;
      const buttonsDiv = this.$refs.buttons;

      // 65 (px) is hardcoded header height
      // + расстояние до questions блока
      // + высота questions блока
      // + 20 (px) is hardcoded margin-bottom from last question
      // + высота buttons блока
      const bottomCoordinateOfButtons =
        65 + questionsDiv.offsetTop + questionsDiv.offsetHeight + 20 + buttonsDiv.offsetHeight;

      if (window.scrollY + window.innerHeight < bottomCoordinateOfButtons) {
        this.isStickySave = true;
      } else {
        this.isStickySave = false;
      }
    }, 50),

    async saveTest() {
      this.isSaving = true;

      // const data = Object.assign({}, this.vacancyTest);
      const data = cloneDeep(this.vacancyTest);
      // data.questions = this.vacancyTestQuestions;
      this.$set(data, 'questions', this.vacancyTestQuestions); // чтобы убрать мутабельность и добавить chart_config дальше

      for (let index = 0; index < data.questions.length; index++) {
        // const question = data.questions[index];
        // const config = this.vacancyTestCharts.find(x => x.questionId == question._id);
        // let newConfig = { ...config };
        // let newConfig = clone(config);

        const questionChartConfig = this.getChartConfigByQuestionId(data.questions[index]._id);
        // console.log(questionChartConfig);
        data.questions[index].chart_config = questionChartConfig;

        // if (newConfig !== undefined) {
        //   if (newConfig.chart_config !== null && newConfig.chart_config.length > 0) {
        //     newConfig.chart_config.forEach((x, index) => {
        //       if (x.variables.length > 0) {
        //         for (let i = 0; i < x.variables.length; i++) {
        //           if (
        //             newConfig.chart_config[index].series[x.variables[i].indexSeries] !== undefined &&
        //             newConfig.chart_config[index].series[x.variables[i].indexSeries].data[x.variables[i].indexData] !==
        //               undefined
        //           ) {
        //             // x.series[v.indexSeries].data[v.indexData] = `{{${v.variableName}}}`;
        //             // console.log(newConfig.chart_config[index].series[v.indexSeries].data[v.indexData]);
        //             newConfig.chart_config[index].series[x.variables[i].indexSeries].data.splice(
        //               x.variables[i].indexData,
        //               1,
        //               `{{${x.variables[i].variableName}}}`
        //             );
        //           }
        //         }

        //         // x.variables.forEach(v => {
        //         //   // console.log(x.series[v.indexSeries]);
        //         //   // console.log(x.series[v.indexSeries].data[v.indexData]);
        //         //   if (
        //         //     x.series[v.indexSeries] !== undefined &&
        //         //     x.series[v.indexSeries].data[v.indexData] !== undefined
        //         //   ) {
        //         //     // x.series[v.indexSeries].data[v.indexData] = `{{${v.variableName}}}`;
        //         //     // console.log(newConfig.chart_config[index].series[v.indexSeries].data[v.indexData]);
        //         //     newConfig.chart_config[index].series[v.indexSeries].data.splice(
        //         //       v.indexData,
        //         //       1,
        //         //       `{{${v.variableName}}}`
        //         //     );
        //         //   }
        //         // });
        //       }
        //     });

        //     console.log(newConfig);

        //     // question.chart_config = newConfig.chart_config;
        //   } else {
        //     question.chart_config = config.chart_config;
        //   }
        // }
      }

      await this.saveVacancyStages();

      await Axios.patch(`/v3/tests/vacancy/${this.vacancyTest.id}`, data)
        .then(() => {
          this.$noty.success(this.$tr('vacancy.preferences.modals.saveSuccess.text'));

          // // isContinue - перейти на следующий этап настройки
          // // TODO: переписать, чтобы этап был универсальным, а не просто video или integration
          // if (isContinue) {
          //   if (!this.modules || !this.modules.length) return false;

          //   const isVideointerviewActive = this.modules.find(item => item.id === 'videointerview' && item.is_active);
          //   const next_section = isVideointerviewActive ? 'video' : 'integration';

          //   this.$router.push({ path: `/vacancies/${this.vacancy_id}/${next_section}` });
          // }
        })
        .catch(() => {
          this.$noty.error(this.$tr('vacancy.preferences.modals.saveFailure.text'));
        })
        .finally(() => {
          this.isSaving = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

::v-deep .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
  overflow: visible;
}

.vacancy-test {
  max-width: 800px;
  padding: 0 0 100px;

  &__questions-views {
    display: flex;
  }

  &__questions-view {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    padding: 20px;
    width: 240px;
    margin-right: 20px;
    border: 1px solid transparent;
    background: #fff;
    transition: border 0.3s;

    &:hover {
      border: 1px solid $green-hover;
      transition: border 0.3s;
    }

    &.active {
      border: 1px solid $green;
      transition: border 0.3s;
    }

    .pointer {
      position: absolute;
      top: 10px;
      left: 10px;
    }

    label {
      display: block;
      margin-top: 20px;
      text-align: center;
      word-wrap: break-word;
      pointer-events: none;
      @include mixin-text-main;
    }
  }

  &__questions-view-icon {
    width: 100px;
  }

  &__options {
    padding: 30px 0;
  }

  &__option {
    margin-bottom: 10px;
    display: flex;

    .input_block {
      width: 45px;
    }
  }

  &__time-wrapper {
    margin-left: 10px;
  }

  &__buttons {
    display: flex;
    padding: 20px;
    background: transparent;
    border: 1px solid transparent;
    justify-content: space-between;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    transition: background 0.3s, box-shadow 0.5s;

    &.sticky {
      background: rgba(255, 255, 255, 0.85);
      position: fixed;
      bottom: 0;
      left: 330px;
      width: 800px;
      box-shadow: $box-shadow;
      border: 1px solid $grey-light;
      backdrop-filter: blur(4px);
      transition: background, 0.3s, box-shadow 0.5s;
    }
  }
}
</style>
